import type {
  GuestSpaceDisplayType,
  GuestSpaceSummary,
} from '@mds/shared/types/guest-booking';

/**
 * The filter chips on `/spaces`. The API's `type` is finer than what a visitor
 * filters by — meeting and training rooms are both "Rooms" — so each chip names
 * the types it covers. Order here is the order of the chips.
 *
 * The type is never printed on a card; it only drives this filter.
 */
export const SPACE_GROUPS = [
  { id: 'desks', label: 'Desks', types: ['desk'] },
  { id: 'rooms', label: 'Rooms', types: ['meeting_room', 'training_room'] },
  { id: 'studio', label: 'Studio', types: ['studio'] },
  { id: 'cafe', label: 'Café hall', types: ['cafe'] },
] as const satisfies readonly {
  id: string;
  label: string;
  types: readonly GuestSpaceDisplayType[];
}[];

export type SpaceGroupId = (typeof SPACE_GROUPS)[number]['id'];

export const spaceGroupOf = (type: GuestSpaceDisplayType): SpaceGroupId | undefined =>
  SPACE_GROUPS.find((group) => (group.types as readonly string[]).includes(type))?.id;

/**
 * One chip per group that has at least one space, with its count. An empty
 * group is left out rather than offered as a filter that shows nothing.
 */
export const spaceGroupOptions = (spaces: readonly GuestSpaceSummary[]) =>
  SPACE_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    count: spaces.filter((space) => spaceGroupOf(space.type) === group.id).length,
  })).filter((option) => option.count > 0);
